import { Grid, GridItem, Wizard } from '@flos/react-ui';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { getCoverableInformationConfig } from './CoverableInformationStepsConfig';
import OnsaProgressBar from '../../shared_components/OnsaProgressBar/OnsaProgressBar';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { useSaveClaimsHistoryInformation } from '../../hooks/useSaveClaimsHistoryInformation';
import type {
    AllCoverablesOnsaDTO,
    ClaimsHistoryInformationToUpdateDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useState } from 'react';
import RemoveModal from '../../shared_components/RemoveModal/RemoveModal';
import {
    allCoverablesOnsaToCoverableDTOArray,
    allCoverablesToCoverableDTOArray,
    getCoverableFromAllOnsaCoverables
} from '../../utils/coverablesUtil';
import ViewExistingInsuranceModal from '../../shared_components/ViewExistingInsuranceModal/ViewExistingInsuranceModal';
import { useCheckCoverableForDoubleInsurance } from '../../recoil/hooks/useCheckCoverableForDoubleInsurance';
import { getPrimaryAccountNumber } from '../../utils/topConfigContextUtil';
import { topConfigContextAtom } from '../../recoil/atoms/topConfigContextAtom';
import CustomerStopModal from '../../shared_components/CustomerStopModal/CustomerStopModal';
import type { CustomerStopCause } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { ConflictingCoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { isLoggedOnSelector } from '../../recoil/selectors/isLoggedOnSelector';
import { identificationAtom } from '../../recoil/atoms/identificationAtom';
import { useGetAIDCustomerStop } from '../../recoil/hooks/useGetAIDCustomerStop';
import _ from 'lodash';
import { isRequestPendingAtom } from '../../recoil/atoms/isRequestPendingAtom';
import { claimsHistorySelector } from '../../recoil/selectors/claimsHistorySelector';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import { onsaProgressSelector } from '../../recoil/selectors/onsaProgressSelector';
import { isCurrentProductBeingRemovedAtom } from '../../recoil/atoms/isCurrentProductBeingRemovedAtom';

const CoverableInformationPage: WizardPageComponent = () => {
    const { saveClaimsHistoryInformation } = useSaveClaimsHistoryInformation();
    const { wizardNextStep, wizardGotoStep, wizardPreviousStep } = useWizardNavigation();
    const [onsaProgress, setOnsaProgress] = useRecoilState(onsaProgressSelector);
    const isCurrentProductBeingRemoved = useRecoilValue(isCurrentProductBeingRemovedAtom);
    const setIsRequestPending = useSetRecoilState(isRequestPendingAtom);
    const onsaCoverables = useRecoilValue(allCoverablesOnsaAtom);
    const shoppingBasketCoverables = useRecoilValue(allCoverablesAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const currentProductConfigSteps = getCoverableInformationConfig(
        currentProduct,
        onsaCoverables
    ).steps.filter((step) => !step.disabled);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showViewInsuranceModal, setShowViewInsuranceModal] = useState(false);
    const topConfigContext = useRecoilValue(topConfigContextAtom);
    const isLoggedOn = useRecoilValue(isLoggedOnSelector);
    const { checkCoverableForDoubleInsurance } = useCheckCoverableForDoubleInsurance();
    const { getAIDCustomerStop } = useGetAIDCustomerStop();
    const [showStopModal, setShowStopModal] = useState<boolean>(false);
    const identificationInfo = useRecoilValue(identificationAtom);
    const [stopCause, setStopCause] = useState<CustomerStopCause>('None');
    const [conflictingCoverable, setConflictingCoverable] = useState<ConflictingCoverableDTO>();
    const onsaCoverableDTOs = allCoverablesOnsaToCoverableDTOArray(onsaCoverables);
    const shoppingBasketCoverableDTOs = allCoverablesToCoverableDTOArray(shoppingBasketCoverables);
    const currentCoverable = getCoverableFromAllOnsaCoverables(
        onsaCoverables,
        currentProduct?.fixedId,
        currentProduct?.lobCode
    );
    const shouldSendClaimsHistory = useRecoilValue(claimsHistorySelector);

    const saveClaims = async (): Promise<void> => {
        if (
            !shouldSendClaimsHistory ||
            [
                'Ulykkesforsikring',
                'Ulykkesforsikring til unge',
                'Børneforsikring',
                'Bilforsikring',
                'Husforsikring',
                'Fritidshusforsikring'
            ].includes(currentProduct.flavourName)
        )
            return;
        const updatedCoverable: ClaimsHistoryInformationToUpdateDTO = {
            productToUpdate: currentProduct.flavourName,
            nonChaProduct: {
                fixedId: currentProduct.fixedId
            }
        };
        await saveClaimsHistoryInformation(updatedCoverable);
    };

    const goNext = useRecoilCallback(
        ({ snapshot }) =>
            async () => {
                const allCoverables: AllCoverablesOnsaDTO =
                    await snapshot.getPromise(allCoverablesOnsaAtom);
                const currentProductConfigSteps = getCoverableInformationConfig(
                    currentProduct,
                    allCoverables
                ).steps.filter((step) => !step.disabled);

                setOnsaProgress((prevStep) => ({
                    ...prevStep,
                    currentStep: prevStep.currentStep + 1
                }));

                if (onsaProgress.currentStep !== currentProductConfigSteps.length) {
                    return;
                }

                try {
                    setIsRequestPending(true);
                    if (isLoggedOn) {
                        const accountNumber = getPrimaryAccountNumber(topConfigContext);
                        const conflictingCov = await checkCoverableForDoubleInsurance(
                            currentCoverable?.lobCode ?? '',
                            currentCoverable?.fixedId ?? '',
                            accountNumber
                        );
                        if (!_.isEmpty(conflictingCov)) {
                            setConflictingCoverable(conflictingCov);
                            setIsRequestPending(false);
                            if (onsaProgress.currentStep !== 1) {
                                goPrevious();
                            } else {
                                setOnsaProgress((prevStep) => ({
                                    ...prevStep,
                                    currentStep: prevStep.currentStep - 1
                                }));
                            }
                            if (
                                onsaCoverableDTOs.length + shoppingBasketCoverableDTOs.length ===
                                1
                            ) {
                                setShowViewInsuranceModal(true);
                                return;
                            } else if (
                                onsaCoverableDTOs.length + shoppingBasketCoverableDTOs.length >=
                                2
                            ) {
                                setShowRemoveModal(true);
                                return;
                            }
                        }
                    } else if (identificationInfo.cpr) {
                        const stopDTO = await getAIDCustomerStop(
                            identificationInfo.cpr,
                            identificationInfo.zipCode
                        );
                        if (stopDTO.stopCause !== 'None') {
                            setStopCause(stopDTO.stopCause);
                            setShowStopModal(true);
                            setIsRequestPending(false);
                            goPrevious();
                            return;
                        }
                    }

                    if (onsaProgress.nextStep === 'ClaimsHistoryPage') {
                        setIsRequestPending(false);
                        wizardGotoStep(onsaProgress.nextStep);
                        return;
                    }
                    void saveClaims().then(() => {
                        setIsRequestPending(false);
                        wizardGotoStep(onsaProgress.nextStep);
                    });
                } catch {
                    setIsRequestPending(false);
                    setOnsaProgress((prevStep) => ({
                        ...prevStep,
                        currentStep: prevStep.currentStep - 1
                    }));
                }
            },
        [allCoverablesOnsaAtom, onsaProgress.currentStep, onsaProgress.nextStep, currentProduct]
    );

    const goPrevious = (): void => {
        if (onsaProgress.currentStep === 1) {
            wizardPreviousStep();
        }
        setOnsaProgress((prevStep) => ({
            ...prevStep,
            currentStep: prevStep.currentStep - 1
        }));
    };

    if (!currentProduct) {
        return <></>;
    }

    return (
        <>
            <Wizard.Container currentStep={onsaProgress?.currentStep}>
                {currentProductConfigSteps.map((step) => {
                    const { id, Component } = step;
                    return (
                        <Wizard.Pane key={id} hideProgress>
                            <Grid>
                                <GridItem span={8} colStart={3}>
                                    {!isCurrentProductBeingRemoved && (
                                        <>
                                            <OnsaProgressBar />
                                            <Component
                                                goNext={() => {
                                                    void goNext();
                                                }}
                                                goPrevious={goPrevious}
                                                wizardNextStep={wizardNextStep}
                                            />
                                        </>
                                    )}
                                </GridItem>
                            </Grid>
                        </Wizard.Pane>
                    );
                })}
            </Wizard.Container>
            {showRemoveModal && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    isRemoveAndContinueModal
                    showModal={showRemoveModal}
                    closeModal={() => {
                        setShowRemoveModal(false);
                    }}
                />
            )}
            {showViewInsuranceModal && conflictingCoverable && currentCoverable && (
                <ViewExistingInsuranceModal
                    coverableToRemove={currentCoverable}
                    conflictingCoverable={conflictingCoverable}
                    showModal={showViewInsuranceModal}
                    closeModal={() => {
                        setShowViewInsuranceModal(false);
                    }}
                />
            )}
            <CustomerStopModal
                stopCause={stopCause}
                showModal={showStopModal}
                closeModal={() => {
                    setShowStopModal(false);
                }}
            />
        </>
    );
};

export default CoverableInformationPage;
